import ImpersonationModal from './ImpersonationModal';
import { InlineMessage } from '../../components';
import http from '../../utils/http';
import React, { Component } from 'react';

const buildErrorMessage = (error, fallback) => {
  const message = error.message || fallback;
  const { code } = error;
  const traceId =
    error.errors && error.errors[0] && error.errors[0].details && error.errors[0].details.traceId;
  const parts = [message];

  if (code && code !== 'UNKNOWN_ERROR') {
    parts.push(`(code: ${code})`);
  }
  if (traceId) {
    parts.push(`(trace: ${traceId})`);
  }
  return parts.join(' ');
};

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  PageHeader,
  StandardParagraph,
  NotValidatedInput,
  Button,
  Loader,
  Table,
} from '@idm/ui-components';
import './style.scss';

class SelectAssociate extends Component {
  static propTypes = {
    associates: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      associateSearchTerm: '',
      associateEIDSearchTerm: '',
      associates: [],
      isLoadingAssociates: false,
      isPaginating: false,
      associatesError: null,
      selectedClient: null,
      paginationCursors: {
        current: null,
        next: null,
        prev: null,
      },
      showImpersonationModal: false,
      selectedAssociate: null,
    };
  }

  componentDidMount() {
    this.loadAssociates();
  }

  loadAssociates = async (cursor = null) => {
    const { match, associates } = this.props;
    const { clientId } = match.params;

    const clientAssoc = associates.find((assoc) => assoc.clientId === clientId);

    if (!clientAssoc) {
      this.props.history.push('/auth/client-management');
      return;
    }

    this.setState({
      selectedClient: clientAssoc.client,
      isLoadingAssociates: true,
    });

    await this.fetchAssociates(clientId, cursor);
  };

  fetchAssociates = async (clientId, cursor = null) => {
    try {
      const searchParams = cursor ? { cursor } : {};
      const response = await http.searchAssociatesByClientId(clientId, searchParams);
      const fetchedAssociates = (response.data && response.data.data) || [];
      const pagination = response.data && response.data.pagination;

      this.setState({
        associates: fetchedAssociates,
        isLoadingAssociates: false,
        isPaginating: false,
        paginationCursors: {
          current: cursor,
          next: pagination && pagination.next ? pagination.next.url : null,
          prev: pagination && pagination.prev ? pagination.prev.url : null,
        },
      });
    } catch (error) {
      this.setState({
        associates: [],
        isLoadingAssociates: false,
        isPaginating: false,
        associatesError: buildErrorMessage(error, 'Failed to load associates'),
      });
    }
  };

  handleBack = async () => {
    this.props.history.push('/auth/client-management');
  };

  handleAssociateSearchChange = (e) => {
    this.setState({
      associateSearchTerm: e.target.value,
    });
  };

  handleAssociateEIDSearchChange = (e) => {
    this.setState({
      associateEIDSearchTerm: e.target.value,
    });
  };

  handleSearch = () => {
    const { associateSearchTerm, associateEIDSearchTerm } = this.state;
    const nameTerm = associateSearchTerm.trim();
    const eidTerm = associateEIDSearchTerm.trim();

    // Both empty - load all associates (bypass Redux guard since we're already on the page)
    if (nameTerm.length === 0 && eidTerm.length === 0) {
      const { clientId } = this.props.match.params;

      this.fetchAssociates(clientId);
      return;
    }

    // Only name search has value
    if (nameTerm.length > 0 && eidTerm.length === 0) {
      if (nameTerm.length < 3) {
        this.setState({
          associatesError: 'Name search requires at least 3 characters',
        });
        return;
      }
      this.searchAssociates(nameTerm, 'query');
      return;
    }

    // Only EID search has value
    if (eidTerm.length > 0 && nameTerm.length === 0) {
      this.searchAssociates(eidTerm, 'employeeId');
      return;
    }

    // Both have values - prioritize EID search
    this.searchAssociates(eidTerm, 'employeeId');
  };

  isSearchDisabled = () => {
    const { associateSearchTerm, associateEIDSearchTerm } = this.state;
    const nameTerm = associateSearchTerm.trim();
    const eidTerm = associateEIDSearchTerm.trim();

    // Disable only if name is entered and it's less than 3 chars (while EID is empty)
    if (nameTerm.length > 0 && nameTerm.length < 3 && eidTerm.length === 0) {
      return true;
    }

    return false;
  };

  searchAssociates = async (searchTerm, searchType = 'query') => {
    const { match } = this.props;
    const { clientId } = match.params;
    const trimmedSearchTerm = searchTerm.trim();

    if (!trimmedSearchTerm) {
      return;
    }

    this.setState({ isLoadingAssociates: true });

    try {
      const searchParams =
        searchType === 'employeeId'
          ? { employeeId: trimmedSearchTerm }
          : { query: trimmedSearchTerm };

      const response = await http.searchAssociatesByClientId(clientId, searchParams);
      const fetchedAssociates = (response.data && response.data.data) || [];
      const pagination = response.data && response.data.pagination;

      this.setState({
        associates: fetchedAssociates,
        isLoadingAssociates: false,
        paginationCursors: {
          current: null,
          next: pagination && pagination.next ? pagination.next.url : null,
          prev: pagination && pagination.prev ? pagination.prev.url : null,
        },
      });
    } catch (error) {
      this.setState({
        associates: [],
        isLoadingAssociates: false,
        associatesError: buildErrorMessage(error, 'Failed to search associates'),
      });
    }
  };

  handleStartImpersonation = async (associate) => {
    const { selectedClient } = this.state;
    const { match } = this.props;
    const { clientId } = match.params;
    const { associateId } = associate;
    const displayName = associate.displayName || `${associate.firstName} ${associate.lastName}`;

    this.setState({
      showImpersonationModal: true,
      selectedAssociate: {
        associateId,
        displayName,
        clientId,
        clientName: selectedClient.name,
      },
    });
  };

  handleCloseModal = () => {
    this.setState({
      showImpersonationModal: false,
      selectedAssociate: null,
    });
  };

  handleImpersonationSuccess = () => {
    window.location.href = '/';
  };

  handleNextPage = () => {
    const { paginationCursors } = this.state;
    const { match } = this.props;
    const { clientId } = match.params;

    if (!paginationCursors.next) {
      return;
    }

    this.setState({ isPaginating: true });
    const url = new URL(paginationCursors.next, window.location.origin);
    const cursor = url.searchParams.get('cursor');

    this.fetchAssociates(clientId, cursor, true);
  };

  handlePreviousPage = () => {
    const { paginationCursors } = this.state;
    const { match } = this.props;
    const { clientId } = match.params;

    if (!paginationCursors.prev) {
      return;
    }

    this.setState({ isPaginating: true });
    const url = new URL(paginationCursors.prev, window.location.origin);
    const cursor = url.searchParams.get('cursor');

    this.fetchAssociates(clientId, cursor, true);
  };

  getTableColumns = () => [
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Surname',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'EID',
      dataIndex: 'eid',
      key: 'eid',
    },
    {
      title: '',
      key: 'action',
      width: 150,
      render: (text, record) => (
        <Button
          buttonType="primary"
          onClick={() => this.handleStartImpersonation(record.associate)}
        >
          Impersonate
        </Button>
      ),
    },
  ];

  getTableData = () => {
    const { associates } = this.state;

    return associates.map((assoc) => {
      let eid = 'N/A';

      if (assoc.externalIdentifiers?.data) {
        const employeeIdObj = assoc.externalIdentifiers.data.find(
          (item) => item.kind === 'employeeId'
        );

        if (employeeIdObj?.data?.employeeId) {
          eid = employeeIdObj.data.employeeId;
        }
      }

      return {
        key: assoc.associateId,
        firstName: assoc.firstName || 'Unknown',
        lastName: assoc.lastName || 'Unknown',
        eid,
        associate: assoc,
      };
    });
  };

  render() {
    const {
      selectedClient,
      associateSearchTerm,
      associateEIDSearchTerm,
      isLoadingAssociates,
      isPaginating,
      associatesError,
      paginationCursors,
      associates,
      showImpersonationModal,
      selectedAssociate,
    } = this.state;

    if (!selectedClient) {
      return null;
    }

    const hasNextPage = !!paginationCursors.next;
    const hasPreviousPage = !!paginationCursors.prev;
    const isInitialLoad = isLoadingAssociates && associates.length === 0;

    return (
      <div className="select-associate-page">
        {showImpersonationModal && selectedAssociate && (
          <ImpersonationModal
            associateId={selectedAssociate.associateId}
            clientId={selectedAssociate.clientId}
            associateDisplayName={selectedAssociate.displayName}
            clientName={selectedAssociate.clientName}
            onCancel={this.handleCloseModal}
          />
        )}
        <div className="page-content">
          <div className="back-button-container">
            <Button buttonType="secondary" onClick={this.handleBack}>
              ← Back to Clients
            </Button>
          </div>
          <PageHeader>Select Associate</PageHeader>

          <div className="info-container">
            <p className="info-header-text">Client Instance</p>
            <div className="info-container-body">
              <div className="section">
                <span className="client-name-text">{selectedClient.name}</span>
              </div>
            </div>
          </div>

          <div className="search-section">
            <div className="search-inputs">
              <NotValidatedInput
                type="text"
                placeholder="Search by name (min 3 characters)"
                value={associateSearchTerm}
                onChange={this.handleAssociateSearchChange}
                disabled={isInitialLoad}
                label="Name"
                name="nameSearch"
              />
              <NotValidatedInput
                type="text"
                placeholder="Search by EID"
                value={associateEIDSearchTerm}
                onChange={this.handleAssociateEIDSearchChange}
                disabled={isInitialLoad}
                label="Employee ID"
                name="eidSearch"
              />
              <Button
                buttonType="primary"
                onClick={this.handleSearch}
                disabled={this.isSearchDisabled() || isInitialLoad}
              >
                Search
              </Button>
            </div>
            {this.isSearchDisabled() && associateSearchTerm.trim().length > 0 && (
              <div className="search-hint">
                <StandardParagraph>Name search requires at least 3 characters</StandardParagraph>
              </div>
            )}
          </div>

          <div className="associates-container">
            {isInitialLoad || isLoadingAssociates ? (
              <div className="loading-message">
                <Loader size="large" />
                <StandardParagraph>Loading associates...</StandardParagraph>
              </div>
            ) : null}
            {!isInitialLoad && !isLoadingAssociates && associatesError ? (
              <InlineMessage type="error" className="associates-error">
                {associatesError}
              </InlineMessage>
            ) : null}
            {!isInitialLoad &&
            !isLoadingAssociates &&
            !associatesError &&
            associates.length === 0 ? (
              <div className="no-results">
                <div className="empty-state-icon">👤</div>
                <PageHeader>No Associates Found</PageHeader>
                <StandardParagraph>
                  {associateSearchTerm || associateEIDSearchTerm
                    ? 'No associates match your search criteria. Try a different search term.'
                    : 'No associates available for this client.'}
                </StandardParagraph>
              </div>
            ) : null}
            {!isInitialLoad && !isLoadingAssociates && !associatesError && associates.length > 0 ? (
              <div className="associates-table">
                {isPaginating && (
                  <div className="pagination-loading-overlay">
                    <Loader size="small" />
                  </div>
                )}
                <Table
                  columns={this.getTableColumns()}
                  data={this.getTableData()}
                  emptyText="No associates found"
                />
              </div>
            ) : null}
          </div>

          {!isLoadingAssociates &&
            !associatesError &&
            associates.length > 0 &&
            (hasNextPage || hasPreviousPage) && (
              <div className="pagination">
                <Button
                  buttonType="secondary"
                  onClick={this.handlePreviousPage}
                  disabled={!hasPreviousPage || isPaginating}
                >
                  Previous
                </Button>
                <Button
                  buttonType="secondary"
                  onClick={this.handleNextPage}
                  disabled={!hasNextPage || isPaginating}
                >
                  Next
                </Button>
              </div>
            )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    associates: state.accountSettingsSwitchClients.main.associates || [],
  };
}

export { SelectAssociate, mapStateToProps };
export default connect(mapStateToProps)(SelectAssociate);
